import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Brain, Box, Loader2, AlertCircle } from 'lucide-react';
import { trackSignup } from '../lib/analytics';

/**
 * Emergent OAuth callback handler.
 * Picks up `#session_id=...` from the URL fragment, exchanges it for our
 * JWT via /api/auth/google/session, then navigates to the dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogleSession } = useAuth();
  const [error, setError] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invocation. Run exactly once.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError('Missing Google session. Please try signing in again.');
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    // Remove the session_id from the URL so it is not left behind in history.
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch (_) {}

    (async () => {
      const result = await loginWithGoogleSession(sessionId);
      if (result.success) {
        // Only treat brand-new users as a CompleteRegistration conversion.
        // The backend returns `is_new_user: true` only the first time this
        // email hits /api/auth/google/session — repeat Google logins are
        // logins, not signups, so we skip them here.
        if (result.user?.is_new_user) {
          // Await the pixel flush before redirecting — otherwise the
          // beacon is cancelled by the immediate navigate().
          try { await trackSignup('google'); } catch (_) {}
        }
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.error || 'Google sign-in failed.');
      }
    })();
  }, [loginWithGoogleSession, navigate]);

  return (
    <div className="min-h-screen bg-bg-dark flex items-center justify-center px-4" data-testid="auth-callback">
      <div className="max-w-md w-full text-center">
        <div className="flex items-center justify-center space-x-3 mb-6">
          <div className="relative">
            <Brain className="w-10 h-10 text-primary" />
            <Box className="w-5 h-5 text-accent absolute -bottom-1 -right-1" />
          </div>
          <span className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            PrintMind AI
          </span>
        </div>
        {!error ? (
          <div className="bg-bg-light border border-text-muted/15 rounded-xl p-8" data-testid="auth-callback-loading">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-text-primary font-semibold mb-1">Signing you in…</p>
            <p className="text-sm text-text-secondary">Finalizing your secure session.</p>
          </div>
        ) : (
          <div className="bg-bg-light border border-error/30 rounded-xl p-8" data-testid="auth-callback-error">
            <AlertCircle className="w-8 h-8 text-error mx-auto mb-3" />
            <p className="text-text-primary font-semibold mb-2">Sign-in failed</p>
            <p className="text-sm text-text-secondary mb-5">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-5 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-lg font-semibold text-sm"
              data-testid="auth-callback-retry-btn"
            >
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
